import { CopyError } from '../copy/text.js';
import { viewerStrings } from '../copy/strings.js';
import type { FieldPresence, QueryField } from '@cdr-clarity/contracts';
import type { QueryCapabilities } from '@cdr-clarity/query-service';
import {
  readViewPreference,
  saveViewPreference,
  type PreferenceStorage,
  type ViewPreference,
} from './view-preferences.js';
import { saveFieldPreset } from './field-preset-preference.js';

const labels = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
export interface FieldSnapshot {
  readonly preference: ViewPreference;
  readonly available: readonly QueryField[];
  readonly visible: readonly QueryField[];
  readonly notice: string | null;
}

/** One instance per application session; every renderer subscribes to the same preference. */
export class FieldState {
  private snapshot: FieldSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly defaults: ViewPreference;
  constructor(
    readonly capabilities: QueryCapabilities,
    presence: readonly FieldPresence[],
    private readonly storage: PreferenceStorage,
    timezone: string,
  ) {
    const empty = new Set(
      presence
        .filter((field) => field.complete && field.total > 0 && field.nonempty === 0)
        .map((field) => field.field),
    );
    const available = [...capabilities.fields].sort(
      (a, b) => labels.compare(a.title, b.title) || labels.compare(a.id, b.id),
    );
    this.defaults = {
      version: 1,
      platform: capabilities.platform,
      catalogVersion: capabilities.catalogVersion,
      visible: available.filter((field) => !empty.has(field.id)).map((field) => field.id),
      presetId: 'custom',
      order: capabilities.fields.map((field) => field.id),
      widths: {},
      sort: [],
      pageSize: 100,
      timezone,
    };
    const saved = readViewPreference(storage, capabilities, this.defaults);
    this.snapshot = { preference: saved.value, available, visible: [], notice: saved.notice };
    this.snapshot = this.makeSnapshot(saved.value, saved.notice);
  }
  getSnapshot = (): FieldSnapshot => this.snapshot;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  private makeSnapshot(preference: ViewPreference, notice: string | null): FieldSnapshot {
    const selected = new Set(preference.visible);
    return {
      preference,
      available: this.snapshot.available,
      visible: this.snapshot.available.filter((field) => selected.has(field.id)),
      notice,
    };
  }
  private publish(value: ViewPreference, notice: string | null): void {
    this.snapshot = this.makeSnapshot(value, notice);
    for (const listener of this.listeners) listener();
  }
  update(patch: Partial<Omit<ViewPreference, 'version' | 'platform' | 'catalogVersion'>>): void {
    const next = { ...this.snapshot.preference, ...patch };
    saveViewPreference(this.storage, next, this.capabilities);
    this.publish(next, null);
  }
  setVisible(id: string, shown: boolean): void {
    if (!this.snapshot.available.some((field) => field.id === id))
      throw new CopyError(viewerStrings.webStateFieldState.fieldIsUnavailableInThisCatalog);
    const visible = new Set(this.snapshot.preference.visible);
    if (shown) visible.add(id);
    else visible.delete(id);
    this.update({ visible: [...visible], presetId: 'custom' });
  }
  selectAll(): void {
    this.update({ visible: this.snapshot.available.map((field) => field.id), presetId: 'custom' });
  }
  unselectAll(): void {
    this.update({ visible: [], presetId: 'custom' });
  }
  applyPreset(id: string): void {
    const preset = this.capabilities.manifest.presets.find((item) => item.id === id);
    if (!preset) throw new CopyError(viewerStrings.webStateFieldState.unknownPlatformFieldPreset);
    this.update({ visible: [...preset.fields], presetId: id });
    saveFieldPreset(this.storage, id);
  }
  reload(): void {
    const saved = readViewPreference(this.storage, this.capabilities, this.defaults);
    this.publish(saved.value, saved.notice);
  }
  reset(): void {
    saveViewPreference(this.storage, this.defaults, this.capabilities);
    this.publish(this.defaults, null);
  }
}
